/**
 * LlmConfigManager — unified LLM provider/model configuration.
 *
 * Answers two questions the platform previously could not:
 *   1. Which model runs a given component, for a given service group?
 *   2. Which configuration layer decided that, and why?
 *
 * The "Which model is in force?" panel exists specifically for (2). Before this,
 * resolution order lived only in a code comment, and two wrong answers were given
 * about which layer was winning.
 *
 * Plan: docs/llm-config-consolidation/consolidation-plan.md
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Divider, Chip, Alert, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, CircularProgress, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

import api, { listLlmConfigs, resolveLlmConfig, saveLlmConfig, deleteLlmConfig } from '../../services/api';

const CAPABILITIES = ['text', 'vision', 'audio', 'function_calling', 'streaming', 'code', 'agentic'];

const EMPTY_FORM = {
  component: '', serviceGroupId: '', tool: '',
  provider: 'vertex_ai', model: '',
  temperature: '', maxTokens: '',
  requiredCapabilities: [], enabled: true, notes: ''
};

// Layers that mean "no explicit config exists for this scope".
const IMPLICIT_LAYERS = ['system_default'];

const LlmConfigManager = ({ token }) => {
  const [configs, setConfigs] = useState([]);
  const [components, setComponents] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [serviceGroups, setServiceGroups] = useState([]);
  const [groupsError, setGroupsError] = useState(null);

  const [probeComponent, setProbeComponent] = useState('');
  const [probeGroupId, setProbeGroupId] = useState('');
  const [probeResult, setProbeResult] = useState(null);
  const [probing, setProbing] = useState(false);
  const [probeError, setProbeError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLlmConfigs(token);
      setConfigs(data.configs || []);
      setComponents(data.components || []);
      setProviders(data.providers || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Service groups drive the scope pickers. The list endpoint has returned three
  // different payload shapes historically, so accept all of them rather than
  // silently rendering an empty dropdown.
  useEffect(() => {
    let cancelled = false;
    api.get('/service-groups', token)
      .then(res => {
        if (cancelled) return;
        const payload = res.data;
        const list = Array.isArray(payload) ? payload
          : Array.isArray(payload?.data) ? payload.data
          : Array.isArray(payload?.serviceGroups) ? payload.serviceGroups : [];
        setServiceGroups(list);
      })
      .catch(() => {
        if (cancelled) return;
        // Non-fatal: platform-level configs are still fully usable without the list.
        setGroupsError('Could not load service groups. Platform-level configuration is unaffected.');
      });
    return () => { cancelled = true; };
  }, [token]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, component: components[0] || '' });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (cfg) => {
    setForm({
      component: cfg.component,
      serviceGroupId: cfg.serviceGroupId?._id || cfg.serviceGroupId || '',
      tool: cfg.tool || '',
      provider: cfg.provider,
      model: cfg.model,
      temperature: cfg.parameters?.temperature ?? '',
      maxTokens: cfg.parameters?.maxTokens ?? '',
      requiredCapabilities: cfg.requiredCapabilities || [],
      enabled: cfg.enabled !== false,
      notes: cfg.notes || ''
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const parameters = {};
      if (form.temperature !== '') parameters.temperature = Number(form.temperature);
      if (form.maxTokens !== '') parameters.maxTokens = Number(form.maxTokens);
      await saveLlmConfig({
        component: form.component,
        serviceGroupId: form.serviceGroupId || null,
        tool: form.tool || null,
        provider: form.provider,
        model: form.model.trim(),
        parameters,
        requiredCapabilities: form.requiredCapabilities,
        enabled: form.enabled,
        notes: form.notes || undefined
      }, token);
      setDialogOpen(false);
      await load();
    } catch (err) {
      // Shown inside the dialog, next to the action that caused it.
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteLlmConfig(deleteTarget._id, token);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleProbe = async () => {
    if (!probeComponent) return;
    setProbing(true);
    setProbeError(null);
    setProbeResult(null);
    try {
      const data = await resolveLlmConfig(
        { component: probeComponent, serviceGroupId: probeGroupId || undefined },
        token
      );
      setProbeResult(data.resolved);
    } catch (err) {
      setProbeError(err.message);
    } finally {
      setProbing(false);
    }
  };

  const formValid = useMemo(
    () => Boolean(form.component && form.provider && form.model.trim()),
    [form.component, form.provider, form.model]
  );

  const scopeLabel = (cfg) => {
    if (cfg.serviceGroupId) {
      const name = cfg.serviceGroupId.name || 'Service group';
      return cfg.tool ? `${name} · ${cfg.tool}` : name;
    }
    return cfg.tool ? `Platform · ${cfg.tool}` : 'Platform default';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box sx={{ maxWidth: '70ch' }}>
          <Typography variant="h5" gutterBottom>LLM Configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Which model runs each part of the platform. Configs resolve narrowest-first:
            service group and tool, then service group, then the component default, then a
            built-in fallback. A component with no config here behaves exactly as before.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={loading}>
          Add config
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {groupsError && <Alert severity="warning" sx={{ my: 2 }} onClose={() => setGroupsError(null)}>{groupsError}</Alert>}

      {/* Which model is in force, and why — the question the resolver exists to answer. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Which model is in force?</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="probe-component-label">Component</InputLabel>
            <Select
              labelId="probe-component-label"
              label="Component"
              value={probeComponent}
              onChange={(e) => setProbeComponent(e.target.value)}
            >
              {components.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 260 }} disabled={serviceGroups.length === 0}>
            <InputLabel id="probe-group-label">Service group</InputLabel>
            <Select
              labelId="probe-group-label"
              label="Service group"
              value={probeGroupId}
              onChange={(e) => setProbeGroupId(e.target.value)}
            >
              <MenuItem value=""><em>Platform (no group)</em></MenuItem>
              {serviceGroups.map(g => (
                <MenuItem key={g._id} value={String(g._id)}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={probing ? <CircularProgress size={16} /> : <SearchIcon />}
            onClick={handleProbe}
            disabled={!probeComponent || probing}
          >
            {probing ? 'Resolving…' : 'Resolve'}
          </Button>
        </Stack>

        {probeError && <Alert severity="error" sx={{ mt: 2 }}>{probeError}</Alert>}

        {probeResult && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip label={`${probeResult.provider} / ${probeResult.model}`} color="primary" />
              <Chip
                label={`decided by: ${probeResult.resolvedFrom}`}
                color={IMPLICIT_LAYERS.includes(probeResult.resolvedFrom) ? 'default' : 'info'}
                variant="outlined"
              />
              {probeResult.providerCorrectedFrom && (
                <Tooltip title="The configured provider could not serve this model id, so it was corrected. The correction is recorded in provenance.">
                  <Chip label={`provider corrected from ${probeResult.providerCorrectedFrom}`} color="warning" />
                </Tooltip>
              )}
            </Stack>
            {IMPLICIT_LAYERS.includes(probeResult.resolvedFrom) && (
              <Typography variant="body2" color="text.secondary">
                No configuration exists for this scope, so the built-in default applies.
              </Typography>
            )}
            {probeResult.capabilityWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>{probeResult.capabilityWarning}</Alert>
            )}
          </Box>
        )}
      </Paper>

      <Typography variant="h6" gutterBottom>Configured overrides</Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : configs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>No configuration overrides yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '60ch', mx: 'auto' }}>
            Every component is running on its built-in default. Add a config to pin a model
            for a component, or for a single service group.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add the first config
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Component</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Provider / model</TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>Params</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map(cfg => (
                <TableRow key={cfg._id} hover>
                  <TableCell><Typography variant="body2">{cfg.component}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{scopeLabel(cfg)}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{cfg.provider} / {cfg.model}</Typography></TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    <Typography variant="body2" color="text.secondary">
                      {cfg.parameters?.temperature ?? '—'} · {cfg.parameters?.maxTokens ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={cfg.enabled === false ? 'Disabled' : 'Active'}
                      color={cfg.enabled === false ? 'default' : 'success'}
                      variant={cfg.enabled === false ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit config">
                      <IconButton aria-label={`Edit ${cfg.component} config`} size="small" onClick={() => openEdit(cfg)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete config">
                      <IconButton aria-label={`Delete ${cfg.component} config`} size="small" onClick={() => setDeleteTarget(cfg)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / edit */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.component ? 'LLM configuration' : 'Add LLM configuration'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel id="form-component-label">Component</InputLabel>
              <Select
                labelId="form-component-label"
                label="Component"
                value={form.component}
                onChange={(e) => setForm(f => ({ ...f, component: e.target.value }))}
              >
                {components.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={serviceGroups.length === 0}>
              <InputLabel id="form-group-label">Service group</InputLabel>
              <Select
                labelId="form-group-label"
                label="Service group"
                value={form.serviceGroupId}
                onChange={(e) => setForm(f => ({ ...f, serviceGroupId: e.target.value }))}
              >
                <MenuItem value=""><em>Platform-wide default</em></MenuItem>
                {serviceGroups.map(g => (
                  <MenuItem key={g._id} value={String(g._id)}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Scoping to a group pins that group's model while others follow the platform
              default — how a pilot cohort keeps a fixed configuration.
            </Typography>

            <TextField
              label="Tool"
              helperText="Optional — narrows this config to one tool inside the component"
              value={form.tool}
              onChange={(e) => setForm(f => ({ ...f, tool: e.target.value }))}
              fullWidth
            />

            <Divider />

            <FormControl fullWidth required>
              <InputLabel id="form-provider-label">Provider</InputLabel>
              <Select
                labelId="form-provider-label"
                label="Provider"
                value={form.provider}
                onChange={(e) => setForm(f => ({ ...f, provider: e.target.value }))}
              >
                {providers.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label="Model"
              required
              placeholder="gemini-2.5-flash"
              helperText="If the provider cannot serve this model id, the provider is corrected and the change recorded"
              value={form.model}
              onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))}
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Temperature" type="number" inputProps={{ step: 0.1, min: 0, max: 2 }}
                value={form.temperature}
                onChange={(e) => setForm(f => ({ ...f, temperature: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Max tokens" type="number" inputProps={{ min: 1 }}
                value={form.maxTokens}
                onChange={(e) => setForm(f => ({ ...f, maxTokens: e.target.value }))}
                fullWidth
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="form-caps-label">Required capabilities</InputLabel>
              <Select
                labelId="form-caps-label"
                label="Required capabilities"
                multiple
                value={form.requiredCapabilities}
                onChange={(e) => setForm(f => ({ ...f, requiredCapabilities: e.target.value }))}
                renderValue={(selected) => selected.join(', ')}
              >
                {CAPABILITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              A model that lacks these is rejected on save rather than failing at generation time.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm(f => ({ ...f, enabled: e.target.checked }))}
                />
              }
              label="Enabled"
            />
            <Typography variant="body2" color="text.secondary">
              A disabled config is skipped and resolution falls through to the next layer.
            </Typography>

            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formValid || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — removing a config silently re-points a component. */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete this configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget && (
              <>
                <strong>{deleteTarget.component}</strong> ({scopeLabel(deleteTarget)}) will fall
                back to the next layer, which may change which model runs it. The deletion is
                recorded in the audit trail.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LlmConfigManager;
